import type { PricingTier, PricingFaq } from '@/types/pricing'
import { NEW_PRICING_ENABLED } from '@/utils/featureFlags'

export const ANNUAL_DISCOUNT_PERCENT = 10

const annualPerMonth = (monthly: number): number =>
  Math.round(monthly * (1 - ANNUAL_DISCOUNT_PERCENT / 100))

export const defaultTiers: PricingTier[] = [
  {
    slug: 'meetings',
    displayName: 'Meetings',
    description: 'Lower-friction entry tier: recordings, transcription, AI file notes.',
    monthlyPrice: 79,
    annualPrice: annualPerMonth(79),
    priceLabel: '/adviser/month',
    features: [
      'In-person & virtual recording',
      'AI transcription (30 hrs/mo)',
      'AI file notes & summaries',
      'Action items & insights',
      'Meeting-based reports',
      'Meeting type templates',
      'Basic client list',
    ],
    modules: ['meetings', 'clients_basic'],
    popular: false,
    isContactSales: false,
    ctaLabel: 'Start Free Trial',
  },
  {
    slug: 'professional',
    displayName: 'Professional',
    description: 'For full-service advice firms: the complete adviser toolkit.',
    monthlyPrice: 149,
    annualPrice: annualPerMonth(149),
    priceLabel: '/adviser/month',
    features: [
      'Everything in Meetings',
      'Sparks AI assistant',
      'Full report builder',
      'AI report generation (80/mo)',
      'Transcription (50 hrs/mo)',
      'Document management',
      'Knowledge base & RAG',
      'Fact find import',
      'Full client management',
      'Portfolios & investments',
      'Cash flow planning',
      'Tax planning & scenarios',
      'Sales pipeline',
      'Ramble dictation',
      'Team channels',
    ],
    modules: [
      'meetings',
      'reports_full',
      'sparks',
      'knowledge_base',
      'clients_full',
      'advisory_tools',
      'pipeline',
      'dictations',
      'channels',
    ],
    popular: true,
    isContactSales: false,
    ctaLabel: 'Start Free Trial',
  },
  {
    slug: 'complete',
    displayName: 'Complete',
    description: 'For client-facing firms needing portal, compliance, and workflows.',
    monthlyPrice: 299,
    annualPrice: annualPerMonth(299),
    priceLabel: '/adviser/month',
    features: [
      'Everything in Professional',
      'Client portal (branded, coming soon)',
      'Compliance & vulnerability',
      'Workflows & approvals',
      'AI reports (100/mo)',
      'Calendar integration',
      'Priority support',
    ],
    modules: [
      'meetings',
      'reports_full',
      'sparks',
      'knowledge_base',
      'clients_full',
      'advisory_tools',
      'pipeline',
      'dictations',
      'channels',
      'client_portal',
      'compliance',
      'workflows',
    ],
    popular: false,
    isContactSales: false,
    ctaLabel: 'Start Free Trial',
  },
  {
    slug: 'enterprise',
    displayName: 'Enterprise',
    description: 'For networks and larger organisations: bespoke arrangements.',
    monthlyPrice: null,
    annualPrice: null,
    priceLabel: '',
    features: [
      'Everything in Complete',
      'Volume discounts',
      'Unlimited transcription',
      'Unlimited AI reports',
      'SSO / SAML',
      'Dedicated support',
      'Custom branding',
      'API access',
      'Data migration',
      'service-level agreement (SLA)',
    ],
    modules: ['*'],
    popular: false,
    isContactSales: true,
    ctaLabel: 'Contact Sales',
    audience: '5+ advisers',
  },
]

export const legacyTiers: PricingTier[] = [
  {
    slug: 'starter',
    displayName: 'Starter',
    description: 'Dip your toe in. Perfect for solo advisers.',
    monthlyPrice: 49,
    annualPrice: 44,
    priceLabel: '/adviser/month',
    features: [
      'All modules included',
      '5 hours transcription',
      '20 AI reports',
      '250 VoIP minutes',
      'Full template library',
      'Client portal access',
      'Email support',
    ],
    modules: ['*'],
    popular: false,
    isContactSales: false,
    ctaLabel: 'Start Free Trial',
  },
  {
    slug: 'professional',
    displayName: 'Professional',
    description: 'For growing firms (2-4 advisers)',
    monthlyPrice: 99,
    annualPrice: 89,
    priceLabel: '/adviser/month',
    features: [
      'Everything in Starter',
      '15 hours transcription',
      '75 AI reports',
      '750 VoIP minutes',
      'Custom templates',
      'Approval workflows',
      'Priority support',
    ],
    modules: ['*'],
    popular: true,
    isContactSales: false,
    ctaLabel: 'Start Free Trial',
  },
  {
    slug: 'team',
    displayName: 'Team',
    description: 'For larger practices (5+ advisers)',
    monthlyPrice: 79,
    annualPrice: 71,
    priceLabel: '/adviser/month',
    features: [
      'Everything in Professional',
      'Unlimited transcription',
      'Unlimited AI reports',
      'Unlimited VoIP',
      'Team management dashboard',
      'Advanced compliance reporting',
      'Dedicated success manager',
    ],
    modules: ['*'],
    popular: false,
    isContactSales: false,
    ctaLabel: 'Start Free Trial',
  },
  {
    slug: 'enterprise',
    displayName: 'Enterprise',
    description: 'For networks and larger organisations',
    monthlyPrice: null,
    annualPrice: null,
    priceLabel: '',
    features: [
      'Everything in Team',
      'Unlimited users',
      'Custom integrations',
      'Dedicated account manager',
      'SSO / SAML',
      'Custom compliance rules',
      'API access',
      'service-level agreement (SLA)',
    ],
    modules: ['*'],
    popular: false,
    isContactSales: true,
    ctaLabel: 'Contact Sales',
  },
]

export const defaultFaqs: PricingFaq[] = [
  {
    question: 'How does the free trial work?',
    answer:
      'Every plan starts with a card-secured 7-day free trial. You enter your card up front so the trial converts seamlessly to paid at day 7, with no second login and no missed access. Cancel any time before then and you will not be charged.',
  },
  {
    question: 'Why is a card required at signup?',
    answer:
      'It removes friction at conversion: when day 7 arrives you keep working without interruption. Card-secured trials convert far more reliably than open trials, which lets us keep entry pricing low.',
  },
  {
    question: "I'm an existing customer: does anything change for me?",
    answer:
      'No. Your plan, price, and feature access are unchanged. Existing customers keep their current pricing as a Legacy Snapshot for as long as they want to stay there. Read the full FAQ on our pricing-update page.',
  },
  {
    question: 'Can I change tier later?',
    answer:
      'Yes. You can upgrade or downgrade at any time. Pro-rata billing handles the difference. Downgrades never delete data: your reports, meetings, and clients stay in your account; you simply lose access to the modules above your new tier until you re-upgrade.',
  },
  {
    question: 'Can I cancel any time?',
    answer:
      'Yes. WealthSpace is monthly rolling. Cancel from your account settings, with no exit fees and no awkward calls. UK customers also benefit from the 14-day cooling-off period under the Consumer Contracts Regulations.',
  },
  {
    question: 'How does per-adviser pricing work?',
    answer:
      'You pay per qualified adviser in your practice. Support staff and paraplanners are included at no extra cost. You pay the same per-adviser price for every adviser on your plan. Enterprise adds volume discounts.',
  },
  {
    question: 'Is there a setup fee?',
    answer:
      'No setup fees on standard plans (Meetings, Professional, or Complete). Enterprise customers may have implementation fees depending on customisation requirements.',
  },
  {
    question: 'What integrations are included?',
    answer:
      'WealthSpace integrates with Zoom, Microsoft Teams, and common back-office platforms. API access and custom integrations are available on Enterprise plans.',
  },
]

export const getTierBySlug = (
  slug: string,
  tiers: PricingTier[] = defaultTiers,
): PricingTier | undefined => tiers.find((t) => t.slug === slug)

const lowestPaidPrice = (tiers: PricingTier[]): number => {
  const prices = tiers
    .map((t) => t.monthlyPrice)
    .filter((p): p is number => typeof p === 'number')
    .sort((a, b) => a - b)
  return prices[0] ?? 0
}

/**
 * Entry-tier monthly price for the *currently active* tier set. Resolved at
 * module load so static strings (comparison data, journey copy) can interpolate
 * a single source of truth without each consumer needing the hook.
 */
export const entryMonthlyPrice: number = lowestPaidPrice(
  NEW_PRICING_ENABLED ? defaultTiers : legacyTiers,
)

/** Tier set that matches the live pricing model (modular vs legacy all-in). */
export const activeTiers: PricingTier[] = NEW_PRICING_ENABLED ? defaultTiers : legacyTiers

/** True when a tier grants any of the given modules (respecting the '*' wildcard). */
const tierHasModule = (tier: PricingTier, moduleIds: string[]): boolean =>
  tier.modules.includes('*') || moduleIds.some((id) => tier.modules.includes(id))

/**
 * Every tier that includes any of the given module IDs, in declaration order
 * (entry → enterprise). Generalises the `includes()` logic in PlanComparison so
 * module showcase pages can derive "which plans include this" from one source.
 */
export const getTiersForModule = (
  moduleIds: string[],
  tiers: PricingTier[] = activeTiers,
): PricingTier[] => tiers.filter((t) => tierHasModule(t, moduleIds))

/**
 * Lowest-priced tier that includes the module: the cheapest plan a buyer needs.
 * Paid tiers sort by monthlyPrice ascending; null-priced (Enterprise) tiers sort
 * last. Returns undefined if no tier includes the module.
 */
export const getLowestTierForModule = (
  moduleIds: string[],
  tiers: PricingTier[] = activeTiers,
): PricingTier | undefined => {
  const including = getTiersForModule(moduleIds, tiers)
  if (including.length === 0) return undefined
  return [...including].sort(
    (a, b) =>
      (a.monthlyPrice ?? Number.POSITIVE_INFINITY) -
      (b.monthlyPrice ?? Number.POSITIVE_INFINITY),
  )[0]
}
